/**
 * A floor set seen in the browser (Three.js), live as its placement changes —
 * Prop Studio's Tune, with no Blender and no GPU worker. A 12 ft corner: the
 * floor in the set, two white walls, lit by an HDRI of the space (its strength
 * and turn) or, with none, a neutral studio. Drag to look around.
 *
 * It is a stand-in for the render, not the render: the placement means what it
 * means to render_room.py's pbr_nodes — one tile is the set's real size times
 * `scale`, turned by `rotation`; the roughness map times `roughness`; the
 * normal map's strength (and the height map's bump) times `relief`; the colour
 * times `brightness` — but the shading is Three's, so judge scale, pattern and
 * tone here and the last word on sheen in a render.
 */

import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import type { TexturePlacement } from '../ui/room3d/photoRender';
import { applyTint } from '../ui/room3d/textureTint';
import { TextureSet, hdriFileUrl, textureMapUrl } from './studioApi';

/** The floor's side, feet: the walls stand on two of its edges. */
const ROOM_FT = 12;
const WALL_FT = 4;
/** The walls' height when the set is on them. */
const WALLS_FT = 8;
const MM_PER_FT = 304.8;

export interface PreviewLight { file: string; strength: number; rotation: number }

export interface FloorPreviewProps {
  set: TextureSet;
  placement: TexturePlacement;
  /** The HDRI to light it by (one of the space's), or null for the studio. */
  light: PreviewLight | null;
  /**
   * Where the set goes: on the floor (a floor, a lawn, a driveway), or on the
   * two walls (drywall, a fence, framing, a door's wood), which then stand
   * full height. Read once, on mount.
   */
  on?: 'floor' | 'walls';
}

type MapKey = 'diffuse' | 'rough' | 'normal' | 'disp' | 'ao';

interface Rig {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  floor: THREE.MeshStandardMaterial;
  walls: THREE.MeshStandardMaterial;
  pmrem: THREE.PMREMGenerator;
  studio: THREE.Texture;
  render: () => void;
}

/** The set's maps, loaded once per set (the placement only re-lays them). */
function loadMaps(set: TextureSet, onLoad: () => void): Partial<Record<MapKey, THREE.Texture>> {
  const loader = new THREE.TextureLoader();
  const out: Partial<Record<MapKey, THREE.Texture>> = {};
  (Object.keys(set.maps) as MapKey[]).forEach((key) => {
    const file = set.maps[key];
    if (!file) return;
    const tex = loader.load(textureMapUrl(set.id, file), onLoad);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.center.set(0.5, 0.5);
    tex.colorSpace = key === 'diffuse' ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    out[key] = tex;
  });
  return out;
}

/** An equirect HDRI as a PMREM environment, turned by `rotation` degrees (rendered on a turned sphere: r160 has no environment rotation). */
function hdriEnvironment(pmrem: THREE.PMREMGenerator, equirect: THREE.Texture, rotation: number): THREE.Texture {
  const scene = new THREE.Scene();
  const sphere = new THREE.Mesh(new THREE.SphereGeometry(100, 64, 32), new THREE.MeshBasicMaterial({ map: equirect, side: THREE.BackSide, toneMapped: false }));
  sphere.rotation.y = THREE.MathUtils.degToRad(rotation);
  scene.add(sphere);
  const env = pmrem.fromScene(scene, 0, 0.1, 200).texture;
  sphere.geometry.dispose();
  (sphere.material as THREE.Material).dispose();
  return env;
}

export const FloorPreview: React.FC<FloorPreviewProps> = ({ set, placement, light, on = 'floor' }) => {
  const host = useRef<HTMLDivElement>(null);
  const rig = useRef<Rig | null>(null);
  const maps = useRef<{ id: string; maps: Partial<Record<MapKey, THREE.Texture>> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Fixed at mount: the scene is built for one or the other.
  const onWalls = useRef(on === 'walls').current;
  const wallFt = onWalls ? WALLS_FT : WALL_FT;

  // The renderer, scene and controls: once.
  useEffect(() => {
    const el = host.current;
    if (!el) return undefined;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    } catch {
      setError('WebGL is not available in this browser.');
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    el.appendChild(renderer.domElement);
    renderer.domElement.style.display = 'block';

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#1d1e22');
    const camera = new THREE.PerspectiveCamera(42, 4 / 3, 0.1, 200);
    camera.position.set(ROOM_FT * 0.95, 5.5, ROOM_FT * 0.95);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(ROOM_FT * 0.4, 0, ROOM_FT * 0.4);
    controls.maxPolarAngle = THREE.MathUtils.degToRad(84);
    controls.minDistance = 2;
    controls.maxDistance = 26;
    controls.enableDamping = false;
    controls.update();

    // With the set on the walls, the floor is a plain mid-grey slab.
    const floor = new THREE.MeshStandardMaterial({ color: onWalls ? 0x8a8c90 : 0xffffff, roughness: onWalls ? 0.85 : 1, metalness: 0 });
    const floorMesh = new THREE.Mesh(new THREE.PlaneGeometry(ROOM_FT, ROOM_FT), floor);
    floorMesh.rotation.x = -Math.PI / 2;
    floorMesh.position.set(ROOM_FT / 2, 0, ROOM_FT / 2);
    scene.add(floorMesh);
    const walls = new THREE.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 0.9, metalness: 0 });
    const back = new THREE.Mesh(new THREE.PlaneGeometry(ROOM_FT, wallFt), walls);
    back.position.set(ROOM_FT / 2, wallFt / 2, 0);
    const side = new THREE.Mesh(new THREE.PlaneGeometry(ROOM_FT, wallFt), walls);
    side.rotation.y = Math.PI / 2;
    side.position.set(0, wallFt / 2, ROOM_FT / 2);
    scene.add(back, side);
    if (onWalls) {
      camera.position.set(ROOM_FT * 0.9, 5, ROOM_FT * 0.9);
      controls.target.set(ROOM_FT * 0.25, wallFt * 0.45, ROOM_FT * 0.25);
      controls.update();
    }

    const pmrem = new THREE.PMREMGenerator(renderer);
    const studio = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;
    scene.environment = studio;

    const render = () => renderer.render(scene, camera);
    controls.addEventListener('change', render);
    const resize = () => {
      const w = Math.max(1, el.clientWidth);
      const h = Math.round(w * 0.72);
      renderer.setSize(w, h, true);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      render();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(el);
    resize();
    rig.current = { renderer, scene, camera, controls, floor, walls, pmrem, studio, render };

    return () => {
      observer.disconnect();
      controls.dispose();
      Object.values(maps.current?.maps ?? {}).forEach((t) => t?.dispose());
      maps.current = null;
      if (scene.environment && scene.environment !== studio) scene.environment.dispose();
      studio.dispose();
      pmrem.dispose();
      floorMesh.geometry.dispose(); back.geometry.dispose(); side.geometry.dispose();
      floor.dispose(); walls.dispose();
      renderer.dispose();
      renderer.domElement.remove();
      rig.current = null;
    };
  }, [onWalls, wallFt]);

  // The set's maps: loaded when the set changes.
  useEffect(() => {
    const r = rig.current;
    if (!r) return;
    if (maps.current?.id === set.id) return;
    Object.values(maps.current?.maps ?? {}).forEach((t) => t?.dispose());
    setLoading(true);
    let left = Object.values(set.maps).filter(Boolean).length;
    const loaded = loadMaps(set, () => { left -= 1; if (left <= 0) setLoading(false); r.render(); });
    maps.current = { id: set.id, maps: loaded };
    const m = onWalls ? r.walls : r.floor;
    m.map = loaded.diffuse ?? null;
    m.roughnessMap = loaded.rough ?? null;
    m.normalMap = loaded.normal ?? null;
    m.aoMap = loaded.ao ?? null;
    // The height map as a bump only when there is no normal map (the normal map carries the relief then).
    m.bumpMap = loaded.normal ? null : loaded.disp ?? null;
    m.needsUpdate = true;
    if (left <= 0) setLoading(false);
  }, [set, onWalls]);

  // The placement: re-laid at once, no reload.
  useEffect(() => {
    const r = rig.current;
    const m = maps.current;
    if (!r || !m) return;
    const k = Math.max(0.01, placement.scale);
    const tileW = Math.max(0.01, set.dimensions[0] / MM_PER_FT) * k;
    const tileH = Math.max(0.01, (set.dimensions[1] || set.dimensions[0]) / MM_PER_FT) * k;
    // Tiled over what it covers: the floor's 12 ft square, or each wall's 12 ft by its height.
    const coverH = onWalls ? WALLS_FT : ROOM_FT;
    Object.values(m.maps).forEach((t) => {
      if (!t) return;
      t.repeat.set(ROOM_FT / tileW, coverH / tileH);
      t.rotation = THREE.MathUtils.degToRad(placement.rotation);
    });
    const mat = onWalls ? r.walls : r.floor;
    mat.roughness = m.maps.rough ? placement.roughness : Math.min(1, 0.7 * placement.roughness);
    mat.normalScale.set(placement.relief, placement.relief);
    mat.bumpScale = 0.02 * placement.relief;
    mat.aoMapIntensity = 0.6;
    mat.color.setScalar(placement.brightness);
    applyTint(mat, placement.tint, placement.tintStrength);
    r.render();
  }, [set, placement, onWalls]);

  // The lighting: the HDRI (fetched once per file, re-turned from the copy kept), or the studio.
  const hdri = useRef<{ file: string; equirect: THREE.Texture } | null>(null);
  const file = light?.file ?? null;
  const rotation = light?.rotation ?? 0;
  useEffect(() => {
    const r = rig.current;
    if (!r) return undefined;
    let cancelled = false;
    const apply = (env: THREE.Texture) => {
      const old = r.scene.environment;
      r.scene.environment = env;
      if (old && old !== r.studio && old !== env) old.dispose();
      r.render();
    };
    if (!file) { apply(r.studio); return undefined; }
    if (hdri.current?.file === file) { apply(hdriEnvironment(r.pmrem, hdri.current.equirect, rotation)); return undefined; }
    const loader = /\.exr$/i.test(file) ? new EXRLoader() : new RGBELoader();
    setLoading(true);
    loader.load(hdriFileUrl(file), (equirect) => {
      if (cancelled) { equirect.dispose(); return; }
      hdri.current?.equirect.dispose();
      hdri.current = { file, equirect };
      apply(hdriEnvironment(r.pmrem, equirect, rotation));
      setLoading(false);
      setError(null);
    }, undefined, () => {
      if (cancelled) return;
      setLoading(false);
      setError(`The HDRI ${file} could not be loaded: the studio light instead.`);
      apply(r.studio);
    });
    return () => { cancelled = true; };
  }, [file, rotation]);
  useEffect(() => () => { hdri.current?.equirect.dispose(); hdri.current = null; }, []);

  // Its strength: only the materials' share of it.
  const strength = light ? light.strength : 1;
  useEffect(() => {
    const r = rig.current;
    if (!r) return;
    r.floor.envMapIntensity = strength;
    r.walls.envMapIntensity = strength;
    r.render();
  }, [strength]);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div ref={host} style={{ width: '100%', borderRadius: 8, overflow: 'hidden', background: '#1d1e22', cursor: 'grab' }} />
      {loading && <div style={{ position: 'absolute', top: 8, left: 10, fontSize: 12, color: '#d9dbe2', background: 'rgba(0,0,0,0.45)', padding: '2px 8px', borderRadius: 6 }}>Loading…</div>}
      {error && <p className="rc-note is-bad">{error}</p>}
    </div>
  );
};

export default FloorPreview;
